import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { CloseGroup, GetClass, GetGroupRef, GetTasksInGroup, RemovePlayerFromGroup } from "../FirebaseQueries";
import { onSnapshot, query } from "firebase/firestore";
import {
	Card,
	Stack,
	LinearProgress,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Alert,
	styled,
	Rating,
	Collapse,
	Grid,
	IconButton,
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Pending, Warning } from "@mui/icons-material";
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BlockIcon from '@mui/icons-material/Block';
import CircularProgress from '@mui/material/CircularProgress';
import { GetScenario, Scenario } from "../Scenarios/Scenarios";
import { Task, Student, IClass } from "../Types";
import TaskAccordion from "../components/TaskAccordion";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime'
import { blueGrey, red } from "@mui/material/colors";
import { useAuth } from "../AuthProvider";

interface Tasks<Task> {
	[key: number]: Task
}

interface GroupDialogProps {
	open: boolean;
	onClose: () => void;
	onCloseGroup: () => void;
}

interface StudentRemove {
	id: string;
	name: string;
}

const StyledRating = styled(Rating)({
	'& .MuiRating-iconFilled': {
		color: "var(--template-palette-primary-main)",
	},
	'& .MuiRating-iconEmpty': {
		color: 'hsl(220, 20%, 88%)',
	},
});


export default function Group() {
	let params = useParams();
	const { currentUser } = useAuth();
	const [scenario, setScenario] = useState<Scenario>({
		index: 0,
		sceneCount: 0,
		tasks: []
	});
	const [tasks, setTasks] = useState<Tasks<Task>>({});
	const [students, setStudents] = useState<Student[]>([]);
	const studentsRef = useRef(students);
	const [scenarioIndex, setScenarioIndex] = useState(0);
	const [ending, setEnding] = useState("");
	const [closeGroupDialogOpen, setCloseGroupDialogOpen] = useState(false);
	const [groupClosed, setGroupClosed] = useState(false);
	const [classId, setClassId] = useState<string | undefined>(undefined);
	const [groupNumber, setGroupNumber] = useState<number>(0);
	const [studentToRemove, setStudentToRemove] = useState<StudentRemove>();
	const [isRemoveStudentDialogOpen, setIsRemoveStudentDialogOpen] = useState<boolean>(false);
	const [canView, setCanView] = useState<boolean | undefined>(undefined);
	const [canEdit, setCanEdit] = useState<boolean | undefined>(undefined);


	dayjs.extend(relativeTime);

	useEffect(() => { // Keep refs updated
		studentsRef.current = students;
	}, [students]);

	useEffect(() => { // Get Group
		const id = params.groupId !== undefined ? params.groupId : "";
		const unsubscribe = onSnapshot(GetGroupRef(id), (snapshot) => {
			if (snapshot.exists()) {
				setGroupNumber(snapshot.data().groupNumber);
				setClassId(snapshot.data().classId);
				setScenarioIndex(snapshot.data().scenarioIndex);
				setEnding(snapshot.data().ending);
				setScenario(GetScenario(snapshot.data().scenarioIndex));
				UpdateStudentsList(snapshot.data().players, snapshot.data().hostId);
				// setIsPlaying(snapshot.data().isPlaying);
				// setIsFinished(snapshot.data().isFinished);
			} else {
				setGroupClosed(true);
				// setIsPlaying(true);
			}
		})

		return unsubscribe;
	}, []);

	useEffect(() => { // Handle Class
		if (classId === undefined || currentUser === null) return;

		if (classId === "") {
			setCanEdit(true);
			setCanView(true);
			return;
		}

		const GetClassData = async () => {
			try {
				const classRef = await GetClass(classId);
				if (classRef.exists()) {
					let classData: IClass = {
						id: classRef.id,
						code: classRef.data().code,
						session: classRef.data().session,
						scenarioIndex: classRef.data().scenarioIndex,
						startTime: classRef.data().startTime,
						endTime: classRef.data().endTime,
						dateTimeCreated: classRef.data().dateTimeCreated,
						ownerId: classRef.data().ownerId,
						editorIds: classRef.data().editorIds,
						viewerIds: classRef.data().viewerIds,
						canEditByLink: classRef.data().canEditByLink,
						canViewByLink: classRef.data().canViewByLink
					}

					setCanView(
						classData.canViewByLink ||
						classData.canEditByLink ||
						classData.viewerIds.includes(currentUser.uid) ||
						classData.editorIds.includes(currentUser.uid) || 
						(classData.ownerId === currentUser.uid)
					);

					setCanEdit(
						classData.canEditByLink ||
						classData.editorIds.includes(currentUser.uid) || 
						(classData.ownerId === currentUser.uid)
					);
				}

			} catch (err) {
				// TODO
				console.log(err);
			}
		};

		GetClassData();
	}, [classId]);

	useEffect(() => { // Get Tasks
		const id = params.groupId !== undefined ? params.groupId : "";
		const q = query(GetTasksInGroup(id));
		const unsubscribe = onSnapshot(q, (snapshot) => {
			if (!snapshot.empty) {
				let newTasks: Tasks<Task> = {};
				snapshot.forEach((doc) => {
					newTasks[doc.data().taskIndex] = AnyToTask(doc.data());
				});
				setTasks(newTasks);
			}
			else {
				// Do something if empty
			}
		});

		return unsubscribe;
	}, []);

	// ---------- Values ---------- //

	function OverallProgress(sceneCount: number, players: Student[]): number {
		if (players === null || players === undefined || players.length === 0 || sceneCount === 0) return 0;
		let playerScenesProgress = players.reduce((sum, p) => sum + p.sceneIndex, 0);
		return Math.ceil(playerScenesProgress / players.length / (scenario.sceneCount - 1) * 100);
	}

	function UpdateStudentsList(players: any, hostId: string) {
		if (players === null) return;
		const keys = Object.keys(players);
		const playersList: Student[] = [];
		keys.forEach((key: string) => {
			playersList.push({
				id: key,
				name: players[key].name,
				role: players[key].role,
				isReady: players[key].isReady,
				sceneIndex: players[key].sceneIndex,
				lastPing: players[key].lastPing?.toDate(),
				isHost: key === hostId
			})
		}
		);
		setStudents(playersList);
	}

	function GetUserCountInRoles(roleIndexes: number[]): number {
		if (roleIndexes === undefined) return 0;

		var count: number = 0;

		studentsRef.current.forEach((item: Student) => {
			if (roleIndexes.includes(item.role)) {
				count++;
			}
		})

		return count;
	}

	function getTasksFinishedCount(): number {
		if (scenario === undefined) return 0;
		var count: number = 0;
		for (let i = 0; i < scenario.tasks.length; i++) {
			if (tasks[scenario.tasks[i].taskIndex] === undefined) return 0;
			if (tasks[scenario.tasks[i].taskIndex].taskFinished) count++;
		}
		return count;
	}

	function AnyToTask(taskData: any): Task {
		const userCount: number = GetUserCountInRoles(taskData.roleIndexes);
		const isDiscussing: boolean = taskData.answersInitial.length >= userCount && userCount > 0 ? true : false;
		const isAnsweringFinal: boolean = (taskData.readyToAnswer.length >= userCount && userCount > 0) ? true : false;

		return {
			isSolo: taskData.isSolo,
			answers: taskData.answers,
			answersInitial: taskData.answersInitial,
			reached: taskData.reached,
			discussing: isDiscussing,
			answeringFinal: isAnsweringFinal,
			taskFinished: taskData.taskFinished,
			answerFinal: taskData.answerFinal,
			chat: taskData.chat,
			timerMax: taskData.timer,
			timerStarted: taskData.timerStarted
		}
	}

	function setAndOpenRemoveStudentDialog(id: string, name: string) {
		setStudentToRemove({ id, name });
		setIsRemoveStudentDialogOpen(true);
	}

	function ConfirmRemoveStudent() {
		if (studentToRemove !== undefined && studentToRemove.id !== "" && params.groupId !== undefined) {
			RemovePlayerFromGroup(params.groupId, studentToRemove.id);
			setIsRemoveStudentDialogOpen(false);
		}
	}

	// ---------- Components ---------- //

	function StudentRoleCell(id: string, name: string, isReady: boolean, lastPing: Date, isHost: boolean) {
		return (
			<Card key={id} sx={{ borderColor: blueGrey[300], padding: "0.5em" }}>
				<Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
					<Stack direction={"row"} alignItems={"center"} spacing={1}>
						<Typography><a href={`/app/students/${id}`}>{name}</a></Typography>
						<Typography>
							{isHost ? "(Host)" : ""}
						</Typography>
					</Stack>
					<Stack direction={"row"} alignItems={"center"} spacing={1}>
						<Typography>{dayjs(lastPing).from(Date.now())}</Typography>
						{isReady ? <CheckCircleIcon></CheckCircleIcon> : <Pending></Pending>}
						<IconButton
							aria-label="close"
							color="inherit"
							size="medium"
							onClick={() => {
								setAndOpenRemoveStudentDialog(id, name);
							}}
							sx={{
								backgroundColor: red[50],
								borderColor: red[100],
								'&:hover': {
									backgroundColor: red[100],
									borderColor: red[200],
								}
							}}
						>
							<BlockIcon fontSize="inherit" sx={{ color: red[300] }} />
						</IconButton>
					</Stack>
				</Stack>
			</Card >
		)
	}

	function CloseGroupDialog(props: GroupDialogProps) {
		const { open, onClose, onCloseGroup } = props;

		const handleClose = () => {
			onClose();
		};

		const handleCloseGroup = () => {
			onCloseGroup();
		}

		return (
			<Dialog onClose={handleClose} open={open}>
				<DialogTitle>Close Group</DialogTitle>
				<DialogContent>
					<DialogContentText>Are you sure? This cannot be undone.</DialogContentText>
					<br />
					<DialogActions>
						<Button onClick={handleClose}>Cancel</Button>
						<Button variant="contained" color="error" onClick={handleCloseGroup}>Close</Button>
					</DialogActions>
				</DialogContent>
			</Dialog>
		);
	}

	// ---------- Component Functions ---------- //

	function openCloseGroupDialog() {
		setCloseGroupDialogOpen(true);
	}

	function closeCloseGroupDialog() {
		setCloseGroupDialogOpen(false);
	}

	function handleCloseGroup() {
		if (params.groupId !== undefined) CloseGroup(params.groupId);
		setCloseGroupDialogOpen(false);
	}


	return (
		<>
			{canView === undefined || canEdit === undefined ?
				<Card>
					<CircularProgress aria-label="Loading…" />
				</Card>
				:
				<>
					{canView === false ?
						<Card>
							<Typography>You do not have permission to view this group.</Typography>
						</Card>
						:
						<Stack spacing={2}>
							{/* ---------- Dialogs and Collapses ---------- */}
							<Dialog open={isRemoveStudentDialogOpen} onClose={() => setIsRemoveStudentDialogOpen(false)}>
								<DialogTitle>Remove player {studentToRemove?.name} from group</DialogTitle>
								<DialogContent sx={{ minWidth: 400 }}>
									<Stack spacing={0.5}>
										<Typography>Are you sure you want to remove this player from the room?</Typography>
										<Typography>They will no longer be able to rejoin, and their answers will be removed.</Typography>
										<Typography>This cannot be undone.</Typography>
									</Stack>
								</DialogContent>
								<DialogActions>
									<Button onClick={() => setIsRemoveStudentDialogOpen(false)}>Cancel</Button>
									<Button onClick={ConfirmRemoveStudent} variant="contained" color="error">Remove Player</Button>
								</DialogActions>
							</Dialog>
							<CloseGroupDialog open={closeGroupDialogOpen} onClose={closeCloseGroupDialog} onCloseGroup={handleCloseGroup}></CloseGroupDialog>
							<Collapse in={groupClosed}>
								<Alert icon={<Warning fontSize="inherit" />} severity="warning"
									action={
										<Button
											variant="outlined"
											aria-label="close"
											color="inherit"
											size="small"
											href="/app/groups"
										>
											Go back to Groups
										</Button>
									}>
									This group was closed.
								</Alert>
							</Collapse>
							<Collapse in={ending !== "" && ending !== undefined}>
								<Alert severity="success">
									<Typography>
										This session has been completed.
									</Typography>
								</Alert>
							</Collapse>
							{/* ---------- Group ---------- */}
							<Card>
								<Stack spacing={2}>
									<Stack direction="row" sx={{ justifyContent: 'space-between' }}>
										<Typography variant="subtitle1" sx={{ textAlign: 'left' }}>Group {groupNumber}</Typography>
										<Stack direction="row" spacing={1}>
											{classId !== "" ?
												<Button href={`/app/classes/progress/${classId}`} variant="contained">Back to Progress</Button> :
												<></>
											}
											{canEdit === false ? <></> :
												<Button onClick={openCloseGroupDialog} variant="contained" color="error" disabled={groupClosed}>Close Group</Button>
											}
										</Stack>
									</Stack>
									<Typography sx={{ textAlign: 'left' }}>Scenario: {scenarioIndex + 1}</Typography>
									<Typography sx={{ textAlign: 'left' }}>Ending: {ending === undefined || ending === "" ? "Not Finished" : ending}</Typography>
									<Box>
										<Grid container spacing={1}>
											<Grid size={2}>
												<Stack spacing={1}>
													<Typography variant="subtitle2" textAlign={"left"}>No Role</Typography>
													{students.filter((student) => student.role === -1).map((student) =>
														StudentRoleCell(student.id, student.name, student.isReady, student.lastPing, student.isHost)
													)}
												</Stack>
											</Grid>
											<Grid size={5}>
												<Stack spacing={1}>
													<Typography variant="subtitle2" textAlign={"left"}>Nurse</Typography>
													{students.filter((student) => student.role === 0).map((student) =>
														StudentRoleCell(student.id, student.name, student.isReady, student.lastPing, student.isHost)
													)}
												</Stack>
											</Grid>
											<Grid size={5}>
												<Stack spacing={1}>
													<Typography variant="subtitle2" textAlign={"left"}>Physiotherapist</Typography>
													{students.filter((student) => student.role === 1).map((student) =>
														StudentRoleCell(student.id, student.name, student.isReady, student.lastPing, student.isHost)
													)}
												</Stack>
											</Grid>

										</Grid>
									</Box>
								</Stack>
							</Card>
							{/* ---------- Status ---------- */}
							<Card>
								<Stack spacing={2}>
									<Typography variant="subtitle1" sx={{ textAlign: 'left' }}>Status</Typography>
									<Box>
										<Accordion>
											<AccordionSummary expandIcon={<ArrowDropDownIcon />}>
												<Stack direction="row" spacing={2} sx={{ width: 1, alignItems: "center" }}>
													<Typography variant="subtitle2">Overall Progress</Typography>
													<Stack sx={{ flexGrow: 1 }} >
														<LinearProgress variant="determinate" value={OverallProgress(scenario.sceneCount, students)} />
													</Stack>
													<Typography color="grey">{OverallProgress(scenario.sceneCount, students)}%</Typography>
												</Stack>
											</AccordionSummary>
											<AccordionDetails>
												{students.map((student) => (
													<Stack key={student.id} direction="row" spacing={2} sx={{ alignItems: "center" }}>
														<Typography variant="subtitle2">{student.name}</Typography>
														<Stack sx={{ flexGrow: 1 }}>
															<LinearProgress variant="determinate" value={student.sceneIndex / (scenario.sceneCount - 1) * 100} />
														</Stack>
														<Typography color="grey">{Math.ceil(student.sceneIndex / (scenario.sceneCount - 1) * 100)}%</Typography>
													</Stack>
												))}
											</AccordionDetails>
										</Accordion>
									</Box>
								</Stack>
							</Card>
							{/* ---------- Tasks ---------- */}
							<Card>
								<Stack spacing={1}>
									<Typography variant="subtitle1" sx={{ textAlign: 'left', mb: 1 }}>Tasks</Typography>
									<Stack direction={"row"} spacing={1} sx={{ mb: 2, alignItems: "center" }}>
										<Typography>Completed: {getTasksFinishedCount()} / {scenario.tasks.length} </Typography>
										<StyledRating
											name="customized-color"
											value={getTasksFinishedCount()}
											precision={1}
											max={scenario.tasks.length}
											icon={<RadioButtonCheckedIcon fontSize="inherit" />}
											emptyIcon={<RadioButtonUncheckedIcon fontSize="inherit" />}
											readOnly
										/>
									</Stack>

									{scenario.tasks.map((task) => {
										return (<TaskAccordion
											key={task.taskIndex}
											taskDef={task}
											taskIndex={task.taskIndex}
											students={students}
											isLive={true}
											isSolo={task.isSolo}
											reached={tasks[task.taskIndex]?.reached}
											discussing={tasks[task.taskIndex]?.discussing}
											answeringFinal={tasks[task.taskIndex]?.answeringFinal}
											taskFinished={tasks[task.taskIndex]?.taskFinished}
											answers={tasks[task.taskIndex]?.answers}
											answersInitial={tasks[task.taskIndex]?.answersInitial}
											answerFinal={tasks[task.taskIndex]?.answerFinal}
											chat={tasks[task.taskIndex]?.chat}
											timerMax={tasks[task.taskIndex]?.timerMax}
											timerStarted={tasks[task.taskIndex]?.timerStarted}
										/>
										)
									}
									)}
								</Stack>
							</Card>
						</Stack>
					}
				</>

			}
		</>
	);
}